import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Package, Truck, CheckCircle, Clock, CreditCard } from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  created: { icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-500/10', label: 'Pending Payment' },
  paid: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-500/10', label: 'Paid' },
  processing: { icon: Package, color: 'text-blue-500', bg: 'bg-blue-500/10', label: 'Processing' },
  shipped: { icon: Truck, color: 'text-purple-500', bg: 'bg-purple-500/10', label: 'Shipped' },
  delivered: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-500/10', label: 'Delivered' }
};

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/orders/my-orders`, {
        withCredentials: true
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSubscription = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/subscriptions/my-subscription`, {
        withCredentials: true
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchSubscription();
  }, [fetchOrders, fetchSubscription]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 border-4 border-[#B8935A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="orders-page">
      <div className="mb-6">
        <h1 className="text-3xl font-heading font-bold text-[#0A0A0A]">Orders & Subscription</h1>
        <p className="text-[#525252] mt-1">Track your purchases and subscription status</p>
      </div>

      {subscription && (
        <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6 mb-8" data-testid="subscription-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#0A0A0A]">Active Subscription</h3>
                <p className="text-sm text-[#525252]">
                  Valid until {format(new Date(subscription.end_date), 'MMM dd, yyyy')}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-[#0A0A0A]">₹{subscription.amount}</p>
              <p className="text-sm text-[#525252]">per year</p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-[#0A0A0A]">Order History</h2>
        
        {orders.length === 0 ? (
          <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-12 text-center" data-testid="no-orders-message">
            <Package className="w-16 h-16 text-[#525252] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#0A0A0A] mb-2">No orders yet</h3>
            <p className="text-[#525252]">Your orders will appear here once you make a purchase.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => {
              const statusInfo = STATUS_CONFIG[order.status] || STATUS_CONFIG.created;
              const StatusIcon = statusInfo.icon;

              return (
                <div
                  key={order.order_id}
                  className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6 hover:border-[#0A0A0A]/15 transition-all"
                  data-testid={`order-${order.order_id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-full ${statusInfo.bg} flex items-center justify-center`}>
                        <StatusIcon className={`w-6 h-6 ${statusInfo.color}`} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-[#0A0A0A]">
                          {order.order_type === 'physical_card' ? 'Physical NFC Card' : 'Subscription Renewal'}
                        </h3>
                        <p className="text-sm text-[#525252]">
                          Order #{order.order_id}
                        </p>
                        <p className="text-sm text-[#525252]">
                          {format(new Date(order.created_at), 'MMM dd, yyyy')}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-[#0A0A0A]">₹{order.amount}</p>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${statusInfo.bg} ${statusInfo.color} mt-2`}>
                        {statusInfo.label}
                      </span>
                    </div>
                  </div>

                  {order.shipping_address && (
                    <div className="mt-4 pt-4 border-t border-[#0A0A0A]/10">
                      <h4 className="text-sm font-medium text-[#0A0A0A] mb-2">Shipping Address</h4>
                      <div className="text-sm text-[#525252]">
                        <p>{order.shipping_name}</p>
                        <p>{order.shipping_address}</p>
                        <p>{order.shipping_city}, {order.shipping_state} - {order.shipping_pincode}</p>
                        <p>{order.shipping_phone}</p>
                      </div>
                      {order.tracking_number && (
                        <p className="text-sm text-[#B8935A] mt-2">
                          Tracking: {order.tracking_number}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;
